import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Pagination } from 'semantic-ui-react'
import _clamp from 'lodash/clamp'
import useUpdateEffect from '../useUpdateEffect/'

const DEFAULT_PAGE_SIZE = 20
const FIRST_PAGE = 1

const getStartIdx = (
  pageSize: number,
  pageNumber: number,
  total: number
): number => Math.min(pageSize * (pageNumber - 1) + 1, total)

const getEndIdx = (startIdx: number, pageSize: number, total: number): number =>
  Math.min(startIdx + pageSize - 1, total)

const getOffset = (
  activePage: number,
  pageSize: number = DEFAULT_PAGE_SIZE
): number => (activePage - 1) * pageSize

const calculatePageNumber = (
  offset: number = 0,
  pageSize: number = DEFAULT_PAGE_SIZE
): number => (pageSize !== 0 ? Math.floor(offset / pageSize) + 1 : 1)

const calculateTotalPages = (
  total: number = 0,
  pageSize: number = DEFAULT_PAGE_SIZE
): number => (pageSize !== 0 ? Math.ceil(total / pageSize) : 0)

const noop = (): void => {}

/**
 * Pagination state object containing current pagination information
 */
export interface PaginationState {
  /** Current active page number (1-indexed) */
  currentPage: number
  /** Total number of pages available */
  totalPages: number
  /** Starting index of items shown on current page */
  startIdx: number
  /** Number of items per page */
  pageSize: number
  /** Whether previous page navigation should be disabled */
  disablePrevious: boolean
  /** Whether next page navigation should be disabled */
  disableNext: boolean
  /** Ending index of items shown on current page */
  endIdx: number
  /** Zero-based offset for current page */
  offset: number
}

/**
 * Parameters for useOffsetPagination hook
 */
export interface UseOffsetPaginationParams {
  /** Total number of items to paginate */
  totalCount: number
  /** Initial offset to start pagination from (default: 0) */
  initialOffset?: number
  /** Number of items per page (default: 20) */
  pageSize?: number
  /** Element ID prefix for pagination components */
  elmId?: string
  /** Callback function called when pagination state changes */
  onPageChange?: (state: PaginationState) => void
}

/**
 * Return value from useOffsetPagination hook
 */
export interface UseOffsetPaginationReturn {
  /** @deprecated Use Paginator instead - Page selection component */
  PageSelection: React.ReactElement
  /** @deprecated Use Paginator instead - Page indicator component */
  PageIndicator: React.ReactElement
  /** Complete paginator component with selection and indicator */
  Paginator: React.ReactElement
  /** Function to update pagination to a specific page */
  updatePagination: (targetPage: number) => void
  /** Function to get offset for a given page and page size */
  getOffset: (activePage: number, pageSize?: number) => number
  /** Function to reset pagination to initial state */
  resetPagination: () => void
  /** Current pagination state */
  paginationState: PaginationState
}

/**
 * Custom hook for managing offset-based pagination with semantic-ui-react components
 *
 * @param params - Configuration object for pagination
 * @returns Object containing pagination components and utilities
 *
 * @example
 * ```tsx
 * const { Paginator, updatePagination, paginationState } = useOffsetPagination({
 *   totalCount: 100,
 *   pageSize: 10,
 *   elmId: 'my-table'
 * })
 * ```
 */
const useOffsetPagination = ({
  totalCount,
  initialOffset = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  elmId = '',
  onPageChange = noop,
}: UseOffsetPaginationParams): UseOffsetPaginationReturn => {
  const [activePage, setActivePage] = useState<number>(
    calculatePageNumber(initialOffset, pageSize)
  )

  const paginationState = useMemo<PaginationState>(() => {
    const totalPages = calculateTotalPages(totalCount, pageSize)
    const startIdx = getStartIdx(pageSize, activePage, totalCount)

    return {
      currentPage: activePage,
      totalPages,
      startIdx,
      pageSize,
      disablePrevious: activePage === FIRST_PAGE,
      disableNext: activePage >= totalPages,
      endIdx: getEndIdx(startIdx, pageSize, totalCount),
      offset: getOffset(activePage, pageSize),
    }
  }, [totalCount, pageSize, activePage])

  useEffect(() => {
    setActivePage(calculatePageNumber(initialOffset, pageSize))
  }, [initialOffset, pageSize])

  useUpdateEffect(() => {
    onPageChange(paginationState)
  }, [paginationState])

  const PageSelection = useMemo<React.ReactElement>(() => {
    return (
      <Pagination
        data-elm-id={`${elmId}-page-selection`}
        activePage={paginationState.currentPage}
        totalPages={paginationState.totalPages}
        firstItem={false}
        lastItem={false}
        pageItem={{ 'data-elm-id': 'number_pagination' }}
        prevItem={{
          content: '\u27E8',
          disabled: paginationState.disablePrevious,
          'data-elm-id': 'prev_pagination',
        }}
        nextItem={{
          content: '\u27E9',
          disabled: paginationState.disableNext,
          'data-elm-id': 'next_pagination',
        }}
        onPageChange={(_e, { activePage }) =>
          setActivePage(activePage as number)
        }
      />
    )
  }, [paginationState, elmId])

  const PageIndicator = useMemo<React.ReactElement>(() => {
    return (
      <div data-elm-id={`${elmId}-page-indicator`}>
        Showing {paginationState.startIdx} - {paginationState.endIdx} of{' '}
        {totalCount} Results
      </div>
    )
  }, [paginationState, totalCount, elmId])

  const Paginator = useMemo<React.ReactElement>(() => {
    return (
      <nav
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
        }}
        data-elm-id={`${elmId}-paginator`}
      >
        {PageSelection}
        {PageIndicator}
      </nav>
    )
  }, [PageSelection, PageIndicator, elmId])

  const updatePagination = useCallback(
    (targetPage: number): void => {
      const totalPages = calculateTotalPages(totalCount, pageSize)
      const newActivePage = _clamp(targetPage, FIRST_PAGE, totalPages)
      setActivePage(newActivePage)
    },
    [totalCount, pageSize]
  )

  const resetPagination = useCallback((): void => {
    setActivePage(calculatePageNumber(initialOffset, pageSize))
  }, [initialOffset, pageSize])

  return {
    PageSelection,
    PageIndicator,
    Paginator,
    updatePagination,
    getOffset,
    resetPagination,
    paginationState,
  }
}

export default useOffsetPagination
